<template>
  <div class="video" :class="modifiers">
    <video
      ref="video"
      :src="source"
      v-bind="$attrs"
      class="video__inner"
      @play="onVideoPlay"
      @pause="onVideoPause"
      @playing="onVideoResume"
      @ended="onVideoEnded"
      @timeupdate="updateProgress"
      @click="togglePlaying"
    />
    <button
      v-if="isPlayVisible"
      type="button"
      class="video__overlay"
      @click="play"
    >
      <BaseIcon name="play" class="video__play" />
      <BaseText
        v-if="label"
        :text="label"
        class="video__label"
        tag="span"
      />
    </button>
    <progress
      v-if="hasProgress"
      ref="progress"
      value="0"
      max="100"
      min="0"
      class="video__progress"
      @click="seek"
    />
  </div>
</template>

<script>
import BaseIcon from '@/components/BaseIcon.vue';
import BaseText from '@/components/BaseText.vue';

export default {
  name: 'BaseVideo',
  inheritAttrs: false,
  components: {
    BaseIcon,
    BaseText,
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    hasPlayButton: {
      type: Boolean,
      default: true,
    },
    hasProgress: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isPlaying: false,
      isStarted: false,
    };
  },
  computed: {
    source() {
      return this.isExternal ? this.src : require(`@/assets/video/${this.src}`); // eslint-disable-line
    },
    isExternal() {
      return /^(blob:)/i.test(this.src) || /^(?:https?:)?\/\//i.test(this.src);
    },
    isPlayVisible() {
      return this.hasPlayButton && !this.isStarted && !this.isPlaying;
    },
    modifiers() {
      return {
        video_playing: this.isPlaying,
        video_started: this.isStarted,
      };
    },
  },
  beforeDestroy() {
    if (this.isPlaying) {
      this.pause();
    }
  },
  methods: {
    play() {
      this.robotMuteOn();
      return this.$refs.video?.play();
    },
    pause() {
      this.robotMuteOff();
      return this.$refs.video?.pause();
    },
    togglePlaying() {
      if (!this.isStarted || !this.hasProgress) return;
      const action = this.isPlaying ? this.pause : this.play;
      action();
    },
    onVideoPlay() {
      if (!this.isStarted) {
        this.isStarted = true;
        this.isPlaying = true;
        this.$emit('play');
      }
    },
    onVideoPause() {
      this.isPlaying = false;
      this.$emit('pause');
    },
    onVideoResume() {
      this.isPlaying = true;
      this.$emit('resume');
    },
    onVideoEnded() {
      this.isStarted = false;
      this.isPlaying = false;
      this.clearTime();
      this.robotMuteOff();
      this.$emit('ended');
    },
    clearTime() {
      const { video } = this.$refs;
      video.currentTime = 0;
    },
    updateProgress() {
      const { progress, video } = this.$refs;
      if (progress && video && video.duration) {
        progress.value = Math.floor((100 / video.duration) * video.currentTime);
      }
    },
    seek(e) {
      const { progress, video } = this.$refs;
      if (video && progress) {
        const percent = e.offsetX / progress.offsetWidth;
        const currentTime = percent * video.duration;
        video.currentTime = currentTime;
        progress.value = Math.floor(percent / 100);
        this.$emit('seek', currentTime);
      }
    },
    robotMuteOn() {
      this.$robot.dialogService.abortRobotReplic();
      this.$robot.dialogService.muteOn();
    },
    robotMuteOff() {
      this.$robot.dialogService.muteOff();
    },
  },
};
</script>

<style lang="stylus">
.video
  position relative
  display block
  width 100%
  padding 0
  background #000

  &:before
    content ''
    display block
    padding-top 62.4% //( 9 / 16 ) * 100%

  &__inner
    display block
    abs top left right bottom
    width 100%
    height @width
    border 0
    outline none
    object-fit contain
    z-index 1

  &__overlay
    abs top left
    width 100%
    height 100%
    background $c_text
    display flex
    flex-flow row wrap
    align-items center
    justify-content center
    text-align center
    color inherit
    outline none
    border 0
    z-index 3

  &__label
    display block
    abs bottom left right
    padding 2em

  &__play
    width 5em
    height @width

  &__progress
    display block
    abs bottom left
    width 100%
    height 1.4em
    border 0
    margin 0
    background $c_text
    border-radius 0
    opacity 1
    z-index 2

    &[value]::-webkit-progress-bar
      background $c_text

    &::-webkit-progress-value
      background-color $c_bright
</style>
